import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  EnhancedSeoHead,
  AuthorBio,
  bjornEvers,
  sverkerCarlsson,
  SeoFunnelLink,
} from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateBreadcrumbSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import { Info, ArrowRight, ExternalLink, CheckCircle } from "lucide-react";

/**
 * OmNackaHemtjanst - About page for nackahemtjanst.se
 *
 * Information about this website and its purpose
 */
const OmNackaHemtjanst: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Om Nacka Hemtjänst – Din guide till hemtjänst i Nacka",
        description:
          "Läs mer om Nacka Hemtjänstguide och hur vi hjälper dig hitta rätt omsorg i Nacka kommun.",
        url: "https://nackahemtjanst.se/om-nacka-hemtjanst",
        datePublished: "2025-12-22",
        dateModified: "2026-01-17",
        authorPerson: {
          name: bjornEvers.name,
          title: bjornEvers.title,
          credentials: bjornEvers.credentials,
          url: bjornEvers.linkedIn,
        },
      }),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
        {
          name: "Om oss",
          url: "https://nackahemtjanst.se/om-nacka-hemtjanst",
        },
      ]),
    [],
  );

  const combinedSchema = useMemo(
    () => combineSchemas([articleSchema, breadcrumbSchema]),
    [articleSchema, breadcrumbSchema],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Om Nacka Hemtjänst – Din guide till hemtjänst i Nacka"
        description="Läs mer om Nacka Hemtjänstguide och hur vi hjälper dig hitta rätt omsorg i Nacka kommun."
        canonicalUrl="https://nackahemtjanst.se/om-nacka-hemtjanst"
        ogType="article"
        keywords={[
          "om nacka hemtjänst",
          "nacka hemtjänstguide",
          "hemtjänst Nacka information",
          "om oss nacka hemtjänst",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-01-17T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={combinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero Section with Glass Effect */}
      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <Info className="w-16 h-16 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-surface-900 tracking-tight mb-6">
            Om Nacka Hemtjänst
          </h1>
          <p className="text-xl md:text-2xl text-surface-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Din oberoende guide till hemtjänst i Nacka kommun. Vi hjälper dig
            hitta rätt omsorg och förstå dina rättigheter.
          </p>
        </div>
      </section>

      {/* About This Site with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-8 md:p-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Vår vision
            </h2>
            <p className="text-lg text-surface-600 mb-8 leading-relaxed">
              Nacka Hemtjänstguide är en oberoende webbplats som syftar till att
              göra det enklare för brukare och anhöriga att navigera
              hemtjänstens värld i Nacka kommun. Vi tror på transparent
              information och att varje person ska ha kunskap om sina
              rättigheter och möjligheter.
            </p>

            <div className="space-y-4 mb-8">
              <h3 className="text-2xl font-bold text-surface-900 mb-4">
                Vad vi gör
              </h3>
              {[
                "Ger dig komplett information om hemtjänst i Nacka",
                "Hjälper dig förstå dina rättigheter enligt lag",
                "Gör det enkelt att jämföra anordnare",
                "Förklarar ansökningsprocessen steg för steg",
                "Ger dig verktyg för att göra välgrundade val",
              ].map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-surface-700">{point}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-8 border-t border-glass-border">
              <h3 className="text-2xl font-bold text-surface-900 mb-4">
                Om Eirtech och Caire
              </h3>
              <p className="text-lg text-surface-600 mb-6 leading-relaxed">
                Nacka Hemtjänstguide drivs av{" "}
                <a
                  href="https://www.eirtech.ai"
                  target="_blank"
                  rel="noreferrer"
                  className="text-emerald-600 font-medium hover:underline inline-flex items-center gap-1"
                >
                  Eirtech
                  <ExternalLink className="w-4 h-4" />
                </a>
                , som utvecklar{" "}
                <SeoFunnelLink
                  href="https://www.caire.se/losningar/privat-hemtjanst"
                  target="_blank"
                  rel="noreferrer"
                  tracking={{
                    sourceSite: "nackahemtjanst.se",
                    sourcePath: "/om-nacka-hemtjanst",
                    targetPath: "/losningar/privat-hemtjanst",
                    anchorCluster: "planeringssystem för hemtjänst",
                    ctaType: "editorial",
                  }}
                  className="text-emerald-600 font-medium hover:underline inline-flex items-center gap-1"
                >
                  Caire
                  <ExternalLink className="w-4 h-4" />
                </SeoFunnelLink>{" "}
                – en AI-driven plattform för hemtjänst. Vårt mål är att göra det
                enklare för brukare och anhöriga att hitta rätt omsorg och för
                anordnare att leverera bättre service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section - E-E-A-T */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-4">
              Vårt team
            </h2>
            <p className="text-lg text-surface-600 max-w-3xl mx-auto">
              Caire grundades av personer med unik kombination av levd
              erfarenhet och teknisk expertis inom hemtjänst.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <AuthorBio author={bjornEvers} variant="full" showCredentials />
            <AuthorBio
              author={sverkerCarlsson}
              variant="full"
              showCredentials
            />
          </div>

          <nav
            aria-label="Författarprofiler"
            className="mt-8 flex flex-wrap justify-center gap-4"
          >
            <Link
              to="/om-oss/bjorn-evers"
              className="rounded-lg border border-emerald-200 px-4 py-2 font-medium text-emerald-700 hover:bg-emerald-50"
            >
              Läs mer om Björn Evers
            </Link>
            <Link
              to="/om-oss/sverker-carlsson"
              className="rounded-lg border border-emerald-200 px-4 py-2 font-medium text-emerald-700 hover:bg-emerald-50"
            >
              Läs mer om Sverker Carlsson
            </Link>
          </nav>

          <div className="mt-12 text-center">
            <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 dark:from-emerald-900/20 dark:via-teal-900/20 dark:to-emerald-900/20 rounded-2xl p-8 border border-emerald-200 dark:border-emerald-700">
              <h3 className="text-2xl font-bold text-surface-900 dark:text-surface-100 mb-4">
                Vår unika konkurrensfördel
              </h3>
              <p className="text-lg text-surface-700 dark:text-surface-300 leading-relaxed max-w-3xl mx-auto">
                Kombinationen av <strong>patientperspektiv</strong> (5 år med
                hemtjänst, 5 000+ besök), <strong>operativ expertis</strong>{" "}
                (medägare av en av Stockholms största privata leverantörer) och{" "}
                <strong>tekniskt ledarskap</strong> (20+ års erfarenhet av
                AI-plattformar) ger oss djup förståelse för både behoven och
                systemproblemen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Note with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl shadow-glass-light p-8">
            <h2 className="text-2xl font-bold text-surface-900 mb-4">
              Viktig information
            </h2>
            <p className="text-lg text-surface-600 leading-relaxed">
              Denna webbplats är en <strong>oberoende guide</strong> och är inte
              en del av Nacka kommun. För officiell information, ansökan och
              tjänster, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline inline-flex items-center gap-1"
              >
                Nacka kommun
                <ExternalLink className="w-4 h-4" />
              </a>{" "}
              direkt.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-12 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-surface-600 mb-4">
              Nacka Hemtjänstguide är en del av{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>
              , där du kan hitta information om hemtjänst i alla Sveriges
              kommuner.
            </p>
          </div>
        </div>
      </section>

      {/* Related Links with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-surface-900 mb-8 text-center">
            Utforska vår guide
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={getPath("/hitta-hemtjanst")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-emerald-600 transition-colors">
                  Hitta hemtjänst
                </h3>
                <p className="text-sm text-surface-600">
                  Jämför anordnare och hitta den som passar dig bäst
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/ansoka")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-blue-600 transition-colors">
                  Ansök om hjälp
                </h3>
                <p className="text-sm text-surface-600">
                  Steg-för-steg guide för att ansöka hos Nacka kommun
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/rattigheter")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-purple-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-purple-600 transition-colors">
                  Dina rättigheter
                </h3>
                <p className="text-sm text-surface-600">
                  Vad du har rätt till enligt lag
                </p>
              </div>
            </Link>
            <Link
              to={getPath("/kontakt")}
              className="flex items-start gap-4 p-6 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border hover:bg-glass-hover transition-all shadow-glass-light"
            >
              <ArrowRight className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-surface-900 hover:text-amber-600 transition-colors">
                  Kontakt
                </h3>
                <p className="text-sm text-surface-600">
                  Kontakta Nacka kommun direkt
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default OmNackaHemtjanst;
